import { showHttpError } from '@calendly/alert'
import { httpRequest } from '@calendly/http-tools'
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'

export const setStep = (step) => (
  {
    type: 'downgrade/setStep',
    payload: step
  }
)

export const fetchBillingInfo = createAsyncThunk(
  'downgrade/fetchBillingInfo',
  async (_params, { dispatch, rejectWithValue }) => {
    const [success, response] = await httpRequest({
      url: '/api/billing/info'
    })

    if (!success) {
      dispatch(showHttpError(response))
      return rejectWithValue(response.message)
    }

    return response
  }
)

const cancelSubscription = createAsyncThunk(
  'downgrade/cancelSubscription',
  async ({ reason, message }, { dispatch, rejectWithValue }) => {
    const [success, response] = await httpRequest({
      url: '/api/billing/subscription/deactivate',
      method: 'POST',
      data: { reason, message }
    })

    if (!success) {
      dispatch(setStep('cancelFailure'))
      return rejectWithValue(response.message)
    }

    dispatch(setStep('cancelSuccess'))
    return response
  }
)

export const cancelSubscriptionWithSurvey = createAsyncThunk(
  'downgrade/cancelSubscriptionWithSurvey',
  async ({ selectedCancelReasons }, { dispatch, rejectWithValue }) => {
    const [success, response] = await httpRequest({
      url: '/api/billing/subscription/deactivate',
      method: 'POST',
      data: { selectedCancelReasons }
    })

    if (!success) {
      dispatch(setStep('cancelFailure'))
      return rejectWithValue(response.message)
    }

    dispatch(setStep('cancelSuccess'))
    return response
  }
)

export const downgradePlan = createAsyncThunk(
  'downgrade/changePlan',
  async ({ tier }, { dispatch, rejectWithValue }) => {
    const [success, response] = await httpRequest({
      url: '/api/billing/subscription/tier',
      method: 'PUT',
      data: { tier }
    })

    if (!success) {
      return rejectWithValue(response.message)
    }

    dispatch(setStep('changeSuccess'))
    return response
  }
)

const downgradeSlice = createSlice({
  name: 'downgrade',
  initialState: {
    step: null,
    billing_info: null,
    error: null,
    loading: false,
    newTier: null,
    pageOneData: null,
    pageTwoData: null
  },
  reducers: {
    resetDowngradeData: (state) => {
      state.step = null
      state.billing_info = null
      state.error = null
      state.loading = false
      state.newTier = null
    },
    setNewTier: (state, { payload }) => {
      state.newTier = payload
    },
    setCancellationSurveyData: (state, { payload }) => {
      switch (payload.pageNumber) {
        case 1:
          state.pageOneData = {
            reason: payload.reason,
            pageOptions: payload.pageOptions
          }
          break
        case 2:
          state.pageTwoData = {
            reason: payload.reason,
            message: payload.message,
            pageOptions: payload.pageOptions
          }
          break
        default:
          state.pageOneData = null
          state.pageTwoData = null
      }
    },
    resetCancellationSurveyData: (state) => {
      state.pageOneData = null
      state.pageTwoData = null
    },
    resetCancellationSurveyPageTwo: (state) => {
      state.pageTwoData = null
    }
  },
  extraReducers: (builder) => {
    builder.addCase('downgrade/setStep', (state, { payload }) => {
      state.step = payload
    })
    builder.addCase(fetchBillingInfo.pending, (state) => {
      state.loading = true
    })
    builder.addCase(fetchBillingInfo.fulfilled, (state, action) => {
      state.loading = false
      state.error = null
      state.billing_info = action.payload
    })
    builder.addCase(fetchBillingInfo.rejected, (state, action) => {
      state.loading = false
      state.error = action.payload
    })
    builder.addCase(cancelSubscription.pending, (state) => {
      state.loading = true
    })
    builder.addCase(cancelSubscription.fulfilled, (state) => {
      state.loading = false
      state.error = null
    })
    builder.addCase(cancelSubscription.rejected, (state) => {
      state.loading = false
    })
    builder.addCase(cancelSubscriptionWithSurvey.pending, (state) => {
      state.loading = true
    })
    builder.addCase(cancelSubscriptionWithSurvey.fulfilled, (state) => {
      state.loading = false
      state.error = null
    })
    builder.addCase(cancelSubscriptionWithSurvey.rejected, (state) => {
      state.loading = false
    })
    builder.addCase(downgradePlan.pending, (state) => {
      state.loading = true
    })
    builder.addCase(downgradePlan.fulfilled, (state) => {
      state.loading = false
      state.error = null
    })
    builder.addCase(downgradePlan.rejected, (state, action) => {
      state.loading = false
      state.error = action.payload
    })
  }
})

export const {
  reducer,
  actions: {
    resetDowngradeData,
    setNewTier,
    setCancellationSurveyData,
    resetCancellationSurveyData,
    resetCancellationSurveyPageTwo,
  }
} = downgradeSlice
